import { useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Umbrella,
    Building2,
    HeartPulse,
    ShieldCheck,
    Clock,
    X,
    Search,
    User,
    ClipboardCheck,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";
import useFAQSchema from "../hooks/useFAQSchema";
import { FAQ_RESOURCES } from "../data/faqs";
import { ARTICLES, ARTICLE_CATEGORIES, getFeaturedArticle } from "../data/articles";

/* ──────────────────────────────────────────────────────────────────
 *  /resources — Knowledge Centre
 *  Structure:
 *    Intro → Featured Article → Article Grid → Filters
 *    → Reporting Conversation lead → Final CTA
 * ────────────────────────────────────────────────────────────────── */

const EYEBROW =
    "font-display font-bold tracking-[0.26em] uppercase text-srm-gold text-[12px] lg:text-[13px]";
const GOLD_RULE = "block h-[3px] w-16 bg-srm-gold";

/* Four filter topics — aligned to ARTICLE_CATEGORIES in /data/articles.js */
const CATEGORIES = [
    {
        icon: HeartPulse,
        name: "Roof Care",
        body: "Assessment cadence, early detection and planned rather than reactive spend.",
    },
    {
        icon: Umbrella,
        name: "Insurance Readiness",
        body: "Claim documentation, insurer expectations and defensible evidence.",
    },
    {
        icon: Building2,
        name: "Safety & Compliance",
        body: "Committee responsibilities, WHS obligations and standardised reporting.",
    },
    {
        icon: ShieldCheck,
        name: "Guardian Program",
        body: "Ongoing, independent stewardship of the roof asset over time.",
    },
];

/* === Hero — Concise introduction ================================= */
function ResourcesHero() {
    const ref = useReveal();
    return (
        <section ref={ref} data-testid="resources-hero" className="bg-white">
            <div className="container-srm pt-12 pb-8 lg:pt-16 lg:pb-10">
                <div className="reveal max-w-[760px] mx-auto text-center">
                    <div className={EYEBROW}>Resources</div>
                    <span aria-hidden className={`mx-auto mt-4 ${GOLD_RULE}`} />
                    <h1
                        data-testid="resources-hero-headline"
                        className="mt-5 font-display font-bold text-[1.95rem] sm:text-[2.3rem] lg:text-[2.7rem] tracking-[-0.024em] leading-[1.1] text-srm-navy text-balance"
                    >
                        Practical guidance for strata committees.
                    </h1>
                    <p className="mt-6 text-[15.5px] lg:text-[17px] text-srm-body leading-[1.8] max-w-[620px] mx-auto">
                        Editorial written by the SRM team for the committees,
                        managers and owners responsible for strata roof assets —
                        governance, insurance, preventative care and long-term
                        guardianship, in plain English.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* === Featured Article ============================================ */
function FeaturedArticle() {
    const ref = useReveal();
    const article = getFeaturedArticle();
    if (!article) return null;
    return (
        <section
            ref={ref}
            data-testid="resources-featured"
            className="bg-white"
        >
            <div className="container-srm py-10 lg:py-14">
                <div className="reveal max-w-5xl mx-auto">
                    <div className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line shadow-[0_18px_45px_rgba(15,40,86,0.10)]">
                        <span
                            aria-hidden
                            className="pointer-events-none absolute left-0 top-0 h-[4px] w-full bg-srm-gold"
                        />
                        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr]">
                            <div className="hidden lg:block relative" style={{ backgroundColor: "#163E7A" }}>
                                <div
                                    aria-hidden
                                    className="pointer-events-none absolute -top-24 -right-24 h-[280px] w-[280px] rounded-full"
                                    style={{ background: "radial-gradient(circle, rgba(201,165,92,0.16) 0%, rgba(201,165,92,0) 70%)" }}
                                />
                                <div className="relative h-full flex flex-col justify-end p-10">
                                    <div className="font-display font-bold tracking-[0.24em] uppercase text-srm-gold text-[11px]">
                                        Featured Article
                                    </div>
                                    <span aria-hidden className="mt-4 block h-[2px] w-10 bg-srm-gold" />
                                    <div className="mt-6 font-display font-bold text-white text-[1.4rem] leading-[1.25]">
                                        {article.featuredEyebrow || article.label}
                                    </div>
                                    <p className="mt-4 text-[14px] text-white/75 leading-[1.75] max-w-[300px]">
                                        {article.summary}
                                    </p>
                                </div>
                            </div>
                            <div className="p-8 lg:p-12">
                                <div className="lg:hidden font-display font-bold tracking-[0.24em] uppercase text-srm-gold text-[11px]">
                                    Featured Article
                                </div>
                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className="font-display font-bold tracking-[0.22em] uppercase text-srm-gold text-[10.5px]">
                                        {article.category}
                                    </span>
                                    <span aria-hidden className="text-srm-navy/25">·</span>
                                    <span className="inline-flex items-center gap-1.5 text-srm-navy/55 font-display font-semibold tracking-[0.18em] uppercase text-[10px]">
                                        <Clock className="h-3 w-3" strokeWidth={1.8} />
                                        {article.readTime}
                                    </span>
                                </div>
                                <span aria-hidden className="mt-4 block h-[2px] w-10 bg-srm-gold" />
                                <h2
                                    data-testid="resources-featured-title"
                                    className="mt-5 font-display font-bold text-srm-navy text-[1.5rem] sm:text-[1.75rem] lg:text-[2rem] tracking-[-0.018em] leading-[1.18]"
                                >
                                    {article.title}
                                </h2>
                                <p className="mt-5 text-[14.5px] lg:text-[15.5px] text-srm-body leading-[1.8]">
                                    {article.summary}
                                </p>
                                <div className="mt-5 inline-flex items-center gap-2 font-display font-semibold tracking-[0.16em] uppercase text-srm-navy/55 text-[11px]">
                                    <User className="h-3.5 w-3.5 text-srm-gold" strokeWidth={1.8} />
                                    {article.author}
                                </div>
                                <div className="mt-8">
                                    <Link
                                        to={`/resources/${article.slug}`}
                                        data-testid="resources-featured-cta"
                                        className="group inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[12px] font-semibold tracking-[0.2em] uppercase px-8 h-[50px] border border-srm-gold shadow-[0_10px_30px_rgba(201,165,92,0.30)] hover:bg-srm-goldDark hover:border-srm-goldDark hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(201,165,92,0.40)] transition-all duration-300"
                                    >
                                        Read Article
                                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* === Article Grid + Search ======================================= */
function ArticleGrid({
    activeCategory,
    onClearFilter,
    query,
    onQueryChange,
    sectionRef,
}) {
    const ref = useReveal();

    const setRefs = (el) => {
        ref.current = el;
        if (sectionRef) sectionRef.current = el;
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return ARTICLES.filter((a) => {
            if (activeCategory && a.category !== activeCategory) return false;
            if (q) {
                const blob = [a.title, a.summary, a.category, a.label, a.intro].join(" ").toLowerCase();
                if (!blob.includes(q)) return false;
            }
            return true;
        });
    }, [activeCategory, query]);

    return (
        <section
            ref={setRefs}
            id="articles"
            data-testid="resources-articles"
            className="bg-white border-t border-srm-line scroll-mt-24"
        >
            <div className="container-srm py-12 lg:py-16">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>All Articles</div>
                    <span aria-hidden className={`mx-auto mt-4 ${GOLD_RULE}`} />
                    <h2 className="mt-5 font-display font-bold text-[1.75rem] sm:text-[2.05rem] lg:text-[2.25rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        Editorial for committees and managers.
                    </h2>

                    {activeCategory && (
                        <div className="mt-6 inline-flex items-center gap-3 rounded-sm bg-srm-navy/[0.04] ring-1 ring-srm-navy/10 px-5 py-2.5">
                            <span className="font-display font-semibold tracking-[0.18em] uppercase text-srm-navy/70 text-[10.5px]">
                                Filtered by
                            </span>
                            <span className="font-display font-bold tracking-[0.18em] uppercase text-srm-navy text-[11px]">
                                {activeCategory}
                            </span>
                            <button
                                type="button"
                                onClick={onClearFilter}
                                data-testid="resources-clear-filter"
                                className="inline-flex items-center gap-1 text-srm-navy/70 hover:text-srm-navy font-display font-semibold tracking-[0.18em] uppercase text-[10.5px] transition-colors"
                            >
                                <X className="h-3 w-3" strokeWidth={2.2} />
                                Clear
                            </button>
                        </div>
                    )}
                </div>

                {/* Search Bar */}
                <div className="mt-7 lg:mt-8 max-w-2xl mx-auto reveal">
                    <label className="relative block">
                        <span className="sr-only">Search articles</span>
                        <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-srm-navy/45">
                            <Search className="h-[18px] w-[18px]" strokeWidth={1.8} />
                        </span>
                        <input
                            type="search"
                            value={query}
                            onChange={(e) => onQueryChange(e.target.value)}
                            data-testid="resources-search-input"
                            placeholder="Search articles..."
                            className="w-full rounded-sm bg-white ring-1 ring-srm-line focus:ring-srm-gold focus:outline-none pl-12 pr-4 h-[52px] text-[14.5px] text-srm-navy placeholder:text-srm-navy/35 transition-all duration-200"
                        />
                    </label>
                </div>

                {/* Result list */}
                {filtered.length === 0 ? (
                    <div className="mt-10 max-w-xl mx-auto text-center text-srm-body" data-testid="resources-empty-state">
                        <p className="font-display font-bold text-srm-navy text-[1.15rem] lg:text-[1.25rem] tracking-[-0.005em]">
                            No articles found yet.
                        </p>
                        <p className="mt-3 text-[14.5px] leading-[1.75]">
                            Adjust your search or clear the filter to see the
                            full library.
                        </p>
                        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                type="button"
                                onClick={() => {
                                    onQueryChange("");
                                    onClearFilter();
                                }}
                                data-testid="resources-empty-view-all"
                                className="inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[11.5px] font-semibold tracking-[0.2em] uppercase px-8 h-[48px] border border-srm-gold hover:bg-srm-goldDark hover:border-srm-goldDark transition-all duration-300"
                            >
                                View all resources
                                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                            </button>
                            <Link
                                to="/contact"
                                data-testid="resources-empty-contact"
                                className="inline-flex items-center justify-center rounded-sm bg-transparent text-srm-navy text-[11.5px] font-semibold tracking-[0.2em] uppercase px-7 h-[48px] border border-srm-navy/25 hover:bg-srm-navy hover:text-white hover:border-srm-navy transition-all duration-300"
                            >
                                Speak with the Team
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="mt-8 lg:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                        {filtered.map((a, i) => (
                            <Link
                                key={a.slug}
                                to={`/resources/${a.slug}`}
                                data-testid={`article-card-${i}`}
                                className="reveal group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-6 lg:p-7 shadow-[0_6px_24px_rgba(15,40,86,0.07)] transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(15,40,86,0.14)]"
                                style={{ transitionDelay: `${(i % 3) * 80}ms` }}
                            >
                                <span
                                    aria-hidden
                                    className="pointer-events-none absolute left-0 top-0 h-[3px] w-16 bg-srm-gold transition-all duration-500 group-hover:w-full"
                                />
                                <div className="flex items-center justify-between gap-3">
                                    <div className="font-display font-bold tracking-[0.22em] uppercase text-srm-gold text-[10.5px]">
                                        {a.category}
                                    </div>
                                    <div className="inline-flex items-center gap-1.5 text-srm-navy/55 font-display font-semibold tracking-[0.18em] uppercase text-[9.5px]">
                                        <Clock className="h-3 w-3" strokeWidth={1.8} />
                                        {a.readTime}
                                    </div>
                                </div>
                                <span aria-hidden className="mt-3.5 block h-[2px] w-10 bg-srm-gold" />
                                <h3 className="mt-3.5 font-display font-bold text-srm-navy text-[1.2rem] lg:text-[1.32rem] tracking-[-0.01em] leading-[1.22] group-hover:text-srm-gold transition-colors duration-300">
                                    {a.title}
                                </h3>
                                <p className="mt-3 text-[13.5px] lg:text-[14px] text-srm-body leading-[1.65] line-clamp-3">
                                    {a.summary}
                                </p>
                                <div className="mt-auto pt-5 inline-flex items-center gap-2 font-display font-semibold tracking-[0.2em] uppercase text-srm-navy text-[11px] group-hover:text-srm-gold transition-colors duration-300">
                                    Read Article
                                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

/* === Filters — Four topic cards ================================== */
function ResourceCategories({ activeCategory, onSelectCategory }) {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="resources-categories"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-12 lg:py-16">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>Filter by Topic</div>
                    <span aria-hidden className={`mx-auto mt-4 ${GOLD_RULE}`} />
                    <h2 className="mt-5 font-display font-bold text-[1.75rem] sm:text-[2.05rem] lg:text-[2.25rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        Four themes for strata roof governance.
                    </h2>
                </div>

                <div className="mt-8 lg:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                    {CATEGORIES.map((c, i) => {
                        const Icon = c.icon;
                        const isActive = activeCategory === c.name;
                        return (
                            <button
                                key={c.name}
                                type="button"
                                onClick={() => onSelectCategory(c.name)}
                                data-testid={`category-card-${i}`}
                                className={`reveal group relative flex flex-col items-start gap-4 overflow-hidden rounded-2xl bg-white ring-1 p-5 lg:p-6 shadow-[0_6px_24px_rgba(15,40,86,0.07)] text-left transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_22px_48px_rgba(15,40,86,0.12)] ${
                                    isActive ? "ring-srm-gold ring-2" : "ring-srm-line"
                                }`}
                                style={{ transitionDelay: `${(i % 4) * 80}ms` }}
                                aria-pressed={isActive}
                            >
                                <span
                                    aria-hidden
                                    className={`pointer-events-none absolute left-0 top-0 h-[3px] bg-srm-gold transition-all duration-500 ${
                                        isActive ? "w-full" : "w-12 group-hover:w-full"
                                    }`}
                                />
                                <span className="inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full border border-srm-gold/45 text-srm-gold transition-all duration-500 group-hover:bg-srm-gold/10">
                                    <Icon className="h-[20px] w-[20px]" strokeWidth={1.6} />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <h3 className="font-display font-bold text-srm-navy text-[1rem] lg:text-[1.08rem] tracking-[-0.005em] leading-snug">
                                        {c.name}
                                    </h3>
                                    <p className="mt-2 text-[12.5px] lg:text-[13px] text-srm-body leading-[1.55]">
                                        {c.body}
                                    </p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* === Reporting Conversation — lead into the SRM reporting standard  */
function ReportingConversation() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="resources-reporting"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-14 lg:py-20">
                <div className="max-w-4xl mx-auto reveal">
                    <div className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-gold/40 p-8 lg:p-12 shadow-[0_6px_24px_rgba(15,40,86,0.07)]">
                        <span
                            aria-hidden
                            className="pointer-events-none absolute left-0 top-0 h-[3px] w-full bg-srm-gold"
                        />
                        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 lg:gap-8 items-start">
                            <span className="inline-flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-full border border-srm-gold/45 text-srm-gold">
                                <ClipboardCheck className="h-[24px] w-[24px]" strokeWidth={1.6} />
                            </span>
                            <div>
                                <div className={EYEBROW}>See the standard behind SRM reporting</div>
                                <span aria-hidden className="mt-4 block h-[2px] w-10 bg-srm-gold" />
                                <h2 className="mt-5 font-display font-bold text-srm-navy text-[1.6rem] sm:text-[1.85rem] lg:text-[2.05rem] tracking-[-0.018em] leading-[1.18]">
                                    A conversation about your roof — and the report behind it.
                                </h2>
                                <p className="mt-5 text-[15px] lg:text-[16.5px] text-srm-body leading-[1.85]">
                                    Every article here reflects how SRM writes and
                                    structures its reporting: observed condition
                                    documented with photographic evidence, reasoning
                                    kept separate from recommendations, and priorities
                                    aligned with the committee&apos;s wider capital plan.
                                </p>
                                <p className="mt-4 text-[15px] lg:text-[16.5px] text-srm-body leading-[1.85]">
                                    If you would like to see what that standard looks
                                    like applied to your building, speak with the SRM
                                    team.
                                </p>
                                <div className="mt-8">
                                    <Link
                                        to="/contact"
                                        data-testid="resources-reporting-cta"
                                        className="group inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[11.5px] font-semibold tracking-[0.2em] uppercase px-8 h-[48px] border border-srm-gold hover:bg-srm-goldDark hover:border-srm-goldDark transition-all duration-300"
                                    >
                                        Speak with the Team
                                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}


/* === Page composition ============================================ */
export default function Resources() {
    usePageMeta({
        title: "Resources — SRM Strata Roof Knowledge Centre",
        description:
            "Independent editorial for strata committees, building managers and owners — insurance, governance, preventative care and roof guardianship, written for the people making the decisions.",
        path: "/resources",
        breadcrumbs: [
            { name: "Home", url: "/" },
            { name: "Resources", url: "/resources" },
        ],
    });
    useFAQSchema("resources", FAQ_RESOURCES);

    const [activeCategory, setActiveCategory] = useState(null);
    const [query, setQuery] = useState("");
    const articlesRef = useRef(null);

    const scrollToArticles = () => {
        setTimeout(() => {
            articlesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
    };

    const handleSelectCategory = (name) => {
        const next = ARTICLE_CATEGORIES.includes(name) && activeCategory !== name ? name : null;
        setActiveCategory(next);
        scrollToArticles();
    };

    const handleClearFilter = () => setActiveCategory(null);

    return (
        <div data-testid="resources-page" className="min-h-screen bg-white">
            <Navbar />
            <main id="main">
                <ResourcesHero />
                <FeaturedArticle />
                <ArticleGrid
                    activeCategory={activeCategory}
                    onClearFilter={handleClearFilter}
                    query={query}
                    onQueryChange={setQuery}
                    sectionRef={articlesRef}
                />
                <ResourceCategories
                    activeCategory={activeCategory}
                    onSelectCategory={handleSelectCategory}
                />
                <ReportingConversation />
            </main>
            <Footer />
        </div>
    );
}
